// One number of the account: which agent answers it, what it costs and until
// when, what came in recently — and the one way to stop it (cancel a billed
// number at the end of its period, remove one that is not billed).

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowRight, FaPhoneAlt } from 'react-icons/fa';
import { cancelNumber, fail, formatDay, formatPrice, removeNumber, resumeNumber, setAgent } from './api';

const statusOf = (item, t) => {
    const billing = item.billing;
    if (billing?.past_due) return ['danger', t('voiceSetup.status.pastDue', 'Paiement en échec')];
    if (billing?.cancel_at_period_end || billing?.status === 'canceling') return ['warning', t('voiceSetup.status.canceling', 'Résilié')];
    if (!item.agent) return ['warning', t('voiceSetup.status.noAgent', 'Aucun agent ne répond')];
    return ['success', t('voiceSetup.status.active', 'Actif')];
};

const NumberCard = ({ item, agents, manage, onChange, onError }) => {
    const { t, i18n } = useTranslation();
    const [busy, setBusy] = useState(false);
    const [confirming, setConfirming] = useState(null);
    const lang = i18n.language;
    const billing = item.billing;
    const canceling = billing && (billing.cancel_at_period_end || billing.status === 'canceling');
    const until = formatDay(billing?.current_period_end, lang);
    const [tone, label] = statusOf(item, t);

    const act = async (call, fallback) => {
        setBusy(true);
        try {
            const res = await call();
            setConfirming(null);
            onChange(res.data);
        } catch (err) {
            onError(fail(err, fallback));
        } finally {
            setBusy(false);
        }
    };

    let source;
    if (item.source === 'subscription') {
        source = canceling
            ? t('voiceSetup.source.canceling', 'Actif jusqu’au {{date}}, puis libéré.', { date: until })
            : t('voiceSetup.source.subscription', 'Abonnement {{price}} / mois · prochaine échéance le {{date}}', {
                price: formatPrice(billing.monthly_price_eur, lang), date: until,
            });
    } else if (item.source === 'assigned') {
        source = t('voiceSetup.source.assigned', 'Attribué par Unanimity, sans abonnement.');
    } else {
        source = t('voiceSetup.source.external', 'Ajouté manuellement : Unanimity ne peut pas vérifier que les appels vers ce numéro arrivent jusqu’à l’agent.');
    }

    let activity;
    if (item.calls_30d > 0) {
        activity = t('voiceSetup.activity.calls', '{{count}} appel(s) sur 30 jours · dernier le {{date}}', {
            count: item.calls_30d, date: formatDay(item.last_call_at, lang),
        });
    } else if (item.last_call_at) {
        activity = t('voiceSetup.activity.quiet', 'Aucun appel sur 30 jours · dernier le {{date}}', { date: formatDay(item.last_call_at, lang) });
    } else {
        activity = t('voiceSetup.activity.none', 'Aucun appel pour l’instant. Appelez ce numéro pour essayer votre agent.');
    }

    return (
        <article className="vs-number">
            <header className="vs-number-header">
                <span className="vs-number-icon" aria-hidden="true"><FaPhoneAlt /></span>
                <div className="vs-number-title">
                    <h4>{item.display}</h4>
                    <p>{source}</p>
                </div>
                <span className={`vs-chip vs-chip--${tone}`}>{label}</span>
            </header>

            {billing?.past_due && (
                <p className="vs-banner vs-banner--danger">
                    {t('voiceSetup.pastDueHint', 'Le dernier prélèvement a échoué : réglez la facture (section Factures) pour garder ce numéro.')}
                </p>
            )}

            <div className="vs-number-grid">
                <div className="vs-field">
                    <label className="vs-field">
                        <span>{t('voiceSetup.answeredBy', 'Agent qui répond')}</span>
                        <select value={item.agent?.id || ''} disabled={!manage || busy}
                            onChange={(e) => act(() => setAgent(item.phone_number, e.target.value),
                                t('voiceSetup.routeError', 'L’agent n’a pas pu être changé.'))}>
                            <option value="">{t('voiceSetup.noAgentOption', 'Aucun agent (le numéro ne répond pas)')}</option>
                            {agents.map((a) => <option key={a.id} value={a.id}>{a.name || t('voiceSetup.unnamedAgent', 'Agent sans nom')}</option>)}
                        </select>
                    </label>
                    {item.agent_missing && <small className="vs-warn">{t('voiceSetup.agentGone', 'Ce numéro renvoyait vers un agent qui n’est pas un agent vocal de ce compte : choisissez-en un.')}</small>}
                    {agents.length === 0 && manage && (
                        <small className="vs-note"><Link className="vs-link" to="/voice-agents">{t('voiceSetup.createAgentFirst', 'Créez d’abord un agent vocal')}</Link></small>
                    )}
                </div>
                <div className="vs-field">
                    <span>{t('voiceSetup.activity.title', 'Activité')}</span>
                    <p className="vs-number-activity">{activity}</p>
                </div>
            </div>

            <footer className="vs-number-footer">
                {item.agent && (
                    <Link className="vs-link" to={`/voice-agents/${item.agent.id}`}>
                        {t('voiceSetup.openAgent', 'Ouvrir l’agent {{name}}', { name: item.agent.name })} <FaArrowRight aria-hidden="true" />
                    </Link>
                )}
                {manage && (
                    <div className="vs-number-actions">
                        {confirming === 'cancel' && (
                            <span className="vs-confirm">
                                {t('voiceSetup.cancelConfirm', 'Le numéro reste actif jusqu’au {{date}}, puis il est libéré.', { date: until })}
                                <button type="button" className="vs-link vs-link--danger" disabled={busy}
                                    onClick={() => act(() => cancelNumber(item.phone_number), t('voiceSetup.cancelError', 'La résiliation a échoué.'))}>
                                    {t('voiceSetup.cancelDo', 'Confirmer la résiliation')}
                                </button>
                                <button type="button" className="vs-link" onClick={() => setConfirming(null)}>{t('voiceSetup.keep', 'Garder le numéro')}</button>
                            </span>
                        )}
                        {confirming === 'remove' && (
                            <span className="vs-confirm">
                                {t('voiceSetup.removeConfirm', 'Le numéro ne répondra plus pour votre compte.')}
                                <button type="button" className="vs-link vs-link--danger" disabled={busy}
                                    onClick={() => act(() => removeNumber(item.phone_number), t('voiceSetup.removeError', 'Le numéro n’a pas pu être retiré.'))}>
                                    {t('voiceSetup.removeDo', 'Retirer le numéro')}
                                </button>
                                <button type="button" className="vs-link" onClick={() => setConfirming(null)}>{t('voiceSetup.cancel', 'Annuler')}</button>
                            </span>
                        )}
                        {!confirming && item.source === 'subscription' && !canceling && (
                            <button type="button" className="vs-link vs-link--danger" onClick={() => setConfirming('cancel')}>
                                {t('voiceSetup.cancelSubscription', 'Résilier')}
                            </button>
                        )}
                        {!confirming && item.source === 'subscription' && canceling && (
                            <button type="button" className="u-btn u-btn--secondary" disabled={busy}
                                onClick={() => act(() => resumeNumber(item.phone_number), t('voiceSetup.resumeError', 'L’abonnement n’a pas pu être repris.'))}>
                                {t('voiceSetup.resume', 'Garder le numéro')}
                            </button>
                        )}
                        {!confirming && item.source !== 'subscription' && (
                            <button type="button" className="vs-link vs-link--danger" onClick={() => setConfirming('remove')}>
                                {t('voiceSetup.remove', 'Retirer')}
                            </button>
                        )}
                    </div>
                )}
            </footer>
        </article>
    );
};

export default NumberCard;
